import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Check, X, AlertTriangle, Clock, Send, CalendarClock, MapPin, Video } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t, formatDate, formatDateTime, formatTime, ageAt } from '../i18n';
import { Card, Button, Badge, Alert, Spinner, EmptyState, Modal, Field, Input } from '../components/ui';

/**
 * What waits for this professional's answer: patients asking to be seen, on
 * their own or referred by a colleague, and sessions patients asked for in
 * hours that need approval (migration 0055).
 *
 * Accepting a patient is what gates booking: until then no session can be
 * created with this professional. Nobody accepts themselves into a
 * professional's calendar — requests are answered only through
 * decide_treatment() (0040) and decide_session_request() (0055).
 */

const Detail = ({ label, children }) => (
  <div className="flex gap-2">
    <dt className="text-slate-500">{label}:</dt>
    <dd className="font-medium text-slate-800">{children}</dd>
  </div>
);

const ApprovalsView = () => {
  const [requests, setRequests] = useState([]);
  const [sessionRequests, setSessionRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // { kind: 'patient' | 'session', row }
  const [declining, setDeclining] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Addressed to the signed-in professional only, admin's included: who asked
    // to see another professional is that professional's business (0038). The
    // functions also name the referring colleague and reach the account holder,
    // whom a professional cannot otherwise look up.
    const [patientResult, sessionResult] = await Promise.all([
      supabase.rpc('my_treatment_requests'),
      supabase.rpc('my_session_requests'),
    ]);
    const failure = patientResult.error ?? sessionResult.error;
    if (failure) setError(failure.message);
    setRequests(patientResult.data ?? []);
    setSessionRequests(sessionResult.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const finish = (answerError) => {
    setBusyId(null);
    setDeclining(null);
    setReason('');
    if (answerError) { setError(answerError.message); return; }
    load();
  };

  const decidePatient = async (request, approve, declineReason = null) => {
    setBusyId(request.treatment_id);
    const { error: decideError } = await supabase.rpc('decide_treatment', {
      p_treatment_id: request.treatment_id,
      p_approve: approve,
      p_reason: declineReason || null,
    });
    finish(decideError);
  };

  const decideSession = async (row, approve, declineReason = null) => {
    setBusyId(row.appointment_id);
    const { error: decideError } = await supabase.rpc('decide_session_request', {
      p_appointment_id: row.appointment_id,
      p_approve: approve,
      p_reason: declineReason || null,
    });
    finish(decideError);
  };

  if (loading) return <Spinner />;

  const nothing = requests.length === 0 && sessionRequests.length === 0;
  const decliningName = declining ? `${declining.row.first_name} ${declining.row.last_name}` : '';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('nav.approvals')}</h1>
        <p className="text-sm text-slate-500">{t('approvals.subtitle')}</p>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {nothing && (
        <Card>
          <EmptyState icon={ClipboardCheck} title={t('approvals.none')} description={t('approvals.noneHint')} />
        </Card>
      )}

      {sessionRequests.length > 0 && (
        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{t('approvals.sessionRequestsTitle')}</h2>
            <p className="text-xs text-slate-500">{t('approvals.sessionRequestsHint')}</p>
          </div>
          {sessionRequests.map((row) => (
            <Card key={row.appointment_id} className="border-amber-200">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-semibold text-slate-900">{row.first_name} {row.last_name}</h3>
                    <Badge tone="warning"><Clock size={12} />{t('appointments.status.requested')}</Badge>
                  </div>
                  <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="flex items-center gap-1.5 font-medium text-slate-900">
                      <CalendarClock size={14} className="text-slate-400" />
                      <span className="first-letter:uppercase">
                        {formatDate(row.starts_at, { weekday: 'long', day: 'numeric', month: 'long' })}
                      </span>
                      {formatTime(row.starts_at)}
                    </span>
                    <span className="flex items-center gap-1 text-slate-600">
                      {row.is_online ? <Video size={13} /> : <MapPin size={13} />}
                      {row.is_online ? t('availability.online') : row.location_name}
                    </span>
                  </p>
                  <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                    <Detail label={t('approvals.requestedAt')}>{formatDateTime(row.requested_at)}</Detail>
                    <Detail label={t('approvals.lapsesAt')}>{formatDateTime(row.lapses_at)}</Detail>
                    <Detail label={t('approvals.accountHolder')}>{row.holder_first_name} {row.holder_last_name}</Detail>
                    <Detail label={t('approvals.contact')}>
                      {row.holder_email}{row.holder_phone && ` · ${row.holder_phone}`}
                    </Detail>
                  </dl>
                </div>
                <div className="flex flex-col gap-2">
                  <Button variant="success" loading={busyId === row.appointment_id} onClick={() => decideSession(row, true)}>
                    <Check size={16} />{t('approvals.confirmSession')}
                  </Button>
                  <Button variant="secondary" loading={busyId === row.appointment_id}
                    onClick={() => setDeclining({ kind: 'session', row })}>
                    <X size={16} />{t('patients.decline')}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </section>
      )}

      {requests.length > 0 && (
        <section className="space-y-3">
          {sessionRequests.length > 0 && (
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{t('approvals.patientRequestsTitle')}</h2>
          )}
          {requests.map((request) => {
            const age = ageAt(request.date_of_birth);
            return (
              <Card key={request.treatment_id} className="border-amber-200">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-900">
                        {request.first_name} {request.last_name}
                      </h3>
                      {age != null && <Badge tone="neutral">{t('clinicalHistory.yearsOld', { age })}</Badge>}
                      <Badge tone="warning"><Clock size={12} />{t('patients.pendingApproval')}</Badge>
                      {request.requested_via === 'referral' ? (
                        <Badge tone="info"><Send size={12} />{t('approvals.referredBy', { name: request.referrer_name ?? '' })}</Badge>
                      ) : (
                        <Badge tone="neutral">{t('approvals.requestedByPatient')}</Badge>
                      )}
                    </div>

                    <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                      <Detail label={t('patients.dateOfBirth')}>{formatDate(request.date_of_birth)}</Detail>
                      <Detail label={t('patients.relationship')}>
                        {request.relationship_to_account === 'self'
                          ? t('patients.self') : request.relationship_to_account}
                      </Detail>
                      <Detail label={t('insurance.coverage')}>
                        {request.plan_name
                          ? `${request.company_name} · ${request.plan_name}`
                          : t('insurance.particular')}
                      </Detail>
                      <Detail label={t('approvals.requestedAt')}>{formatDateTime(request.requested_at)}</Detail>
                      <Detail label={t('approvals.accountHolder')}>
                        {request.holder_first_name} {request.holder_last_name}
                      </Detail>
                      <Detail label={t('approvals.contact')}>
                        {request.holder_email}
                        {request.holder_phone && ` · ${request.holder_phone}`}
                      </Detail>
                    </dl>

                    {request.referral_note && (
                      <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
                        <p className="text-xs font-medium text-slate-500">{t('approvals.referralNote')}</p>
                        <p className="mt-1 whitespace-pre-wrap text-slate-800">{request.referral_note}</p>
                      </div>
                    )}

                    {request.billing_mode === 'particular' && (
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700">
                        <AlertTriangle size={12} />{t('approvals.particularHint')}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button variant="success" loading={busyId === request.treatment_id}
                      onClick={() => decidePatient(request, true)}>
                      <Check size={16} />{t('patients.approve')}
                    </Button>
                    <Button variant="secondary" loading={busyId === request.treatment_id}
                      onClick={() => setDeclining({ kind: 'patient', row: request })}>
                      <X size={16} />{t('patients.decline')}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </section>
      )}

      <Modal
        open={Boolean(declining)}
        title={t(declining?.kind === 'session' ? 'approvals.sessionDeclineTitle' : 'approvals.declineTitle')}
        onClose={() => setDeclining(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeclining(null)}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              onClick={() => (declining.kind === 'session'
                ? decideSession(declining.row, false, reason)
                : decidePatient(declining.row, false, reason))}
            >
              {t('patients.decline')}
            </Button>
          </>
        }
      >
        {declining && (
          <>
            <p className="mb-3 text-sm text-slate-600">
              {declining.kind === 'session'
                ? t('approvals.sessionDeclineBody', {
                  name: decliningName,
                  when: `${formatDate(declining.row.starts_at, { weekday: 'long', day: 'numeric', month: 'long' })} ${formatTime(declining.row.starts_at)}`,
                })
                : t('approvals.declineBody', { name: decliningName })}
            </p>
            <Field
              label={t('approvals.declineReason')}
              hint={t(declining.kind === 'session' ? 'approvals.sessionDeclineReasonHint' : 'approvals.declineReasonHint')}
            >
              <Input value={reason} onChange={(event) => setReason(event.target.value)} />
            </Field>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ApprovalsView;
